import React, { useState, useMemo } from 'react';
import { useTheme } from '../../context/ThemeContext';
import {
  parseMobileStyles,
  resolveElementStyle,
  buildBackgroundStyle,
  buildBadgeStyle,
  buildPanelStyle
} from '../../utils/mobileStyleUtils';

export default function MobileAuthScreen({ setUser, onSwitchToDesktop }) {
  const { tenantConfig } = useTheme();
  const [isLogin, setIsLogin] = useState(true);

  // Mobile Web Styling Resolution
  const mobileStyles = useMemo(() => parseMobileStyles(tenantConfig?.mobile_styles), [tenantConfig?.mobile_styles]);
  const pageKey = 'end_user:login';
  const bannerConfig = useMemo(() => resolveElementStyle(mobileStyles, pageKey, 'banner').style, [mobileStyles, pageKey]);
  const bgConfig = useMemo(() => resolveElementStyle(mobileStyles, pageKey, 'background').style, [mobileStyles, pageKey]);
  const panelConfig = useMemo(() => resolveElementStyle(mobileStyles, pageKey, 'content_panels').style, [mobileStyles, pageKey]);
  const badgeConfig = useMemo(() => resolveElementStyle(mobileStyles, pageKey, 'badges').style, [mobileStyles, pageKey]);
  const typoConfig = useMemo(() => resolveElementStyle(mobileStyles, pageKey, 'typography').style, [mobileStyles, pageKey]);

  const customBgStyle = useMemo(() => buildBackgroundStyle(bgConfig), [bgConfig]);
  const customPanelStyle = useMemo(() => buildPanelStyle(panelConfig), [panelConfig]);
  const customBadgeStyle = useMemo(() => buildBadgeStyle(badgeConfig), [badgeConfig]);
  
  // Inputs
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  // Status
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const primaryColor = tenantConfig?.branding_colors?.primary || '#d97706';
  const allowSignup = tenantConfig?.allow_public_signup !== false;

  const isDev = window.location.port === '3000';
  const API_BASE = isDev ? `http://${window.location.hostname}:8000` : '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    if (!isLogin && !allowSignup) {
      setError('Public registration is currently disabled. Please contact your organization administrator.');
      setLoading(false);
      return;
    }

    const endpoint = isLogin ? '/api/v1/auth/login' : '/api/v1/auth/signup';
    const payload = isLogin
      ? { email: email.trim(), password, remember: rememberMe }
      : { email: email.trim(), password, role: 'END_USER', first_name: firstName.trim(), last_name: lastName.trim() };

    try {
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Domain': window.location.hostname
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Authentication failed. Please check your credentials.');
      }

      if (isLogin) {
        if (rememberMe) {
          localStorage.setItem('token', data.access_token);
          sessionStorage.removeItem('token');
        } else {
          sessionStorage.setItem('token', data.access_token);
          localStorage.removeItem('token');
        }
        setUser(data.user);
      } else {
        setMessage(data.message || 'Account created! Please check your email to verify your account or sign in.');
        setIsLogin(true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      data-testid="mobile-auth-screen-container"
      style={{
        minHeight: '100dvh',
        backgroundColor: '#0a0e17',
        color: typoConfig?.body_color || '#f8fafc',
        fontFamily: typoConfig?.font_family || "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '28px 20px',
        boxSizing: 'border-box',
        position: 'relative',
        overflowX: 'hidden',
        ...customBgStyle
      }}
    >
      {/* Background Ambience Glow */}
      <div style={{
        position: 'absolute',
        top: '-100px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '300px',
        height: '240px',
        borderRadius: '50%',
        backgroundColor: typoConfig?.accent_color || primaryColor,
        opacity: 0.12,
        filter: 'blur(80px)',
        pointerEvents: 'none'
      }} />

      {/* Main Content Card Container */}
      <div style={{
        width: '100%',
        maxWidth: '380px',
        margin: 'auto 0',
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Brand Header & Constrained Logo */}
        <div style={{ textAlign: 'center', marginBottom: '22px' }}>
          {bannerConfig?.custom_logo_url || tenantConfig?.logo_url ? (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '10px 18px',
              borderRadius: '16px',
              backgroundColor: 'rgba(255, 255, 255, 0.04)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              marginBottom: '14px',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
              transform: `translate(${bannerConfig?.logo_offset_x || 0}px, ${bannerConfig?.logo_offset_y || 0}px)`
            }}>
              <img 
                src={bannerConfig?.custom_logo_url || tenantConfig.logo_url} 
                alt={tenantConfig?.brand_name || 'Logo'} 
                style={{
                  height: `${bannerConfig?.logo_height || 48}px`,
                  maxHeight: `${bannerConfig?.logo_height || 48}px`,
                  maxWidth: '180px',
                  width: 'auto',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            </div>
          ) : (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: `${bannerConfig?.logo_height || 52}px`,
              height: `${bannerConfig?.logo_height || 52}px`,
              borderRadius: '16px',
              backgroundColor: typoConfig?.accent_color || primaryColor,
              color: '#ffffff',
              marginBottom: '14px',
              boxShadow: '0 8px 20px rgba(0, 0, 0, 0.35)',
              transform: `translate(${bannerConfig?.logo_offset_x || 0}px, ${bannerConfig?.logo_offset_y || 0}px)`
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
                <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
                <path d="M4 22h16" />
                <path d="M10 14.66V17c0 .55-.45 1-1 1H4v2h16v-2h-5c-.55 0-1-.45-1-1v-2.34" />
                <path d="M12 2a6 6 0 0 1 6 6v5a6 6 0 0 1-6 6 6 6 0 0 1-6-6V8a6 6 0 0 1 6-6z" />
              </svg>
            </div>
          )}

          <h1 style={{
            fontSize: `${bannerConfig?.title_font_size || 22}px`,
            fontWeight: 800,
            color: bannerConfig?.title_color || typoConfig?.heading_color || '#ffffff',
            margin: '0 0 6px 0',
            letterSpacing: `${bannerConfig?.title_letter_spacing || -0.3}px`
          }}>
            {tenantConfig?.brand_name || 'TrophyCoin'}
          </h1>
          <p style={{
            fontSize: '13px',
            color: bannerConfig?.subtitle_color || typoConfig?.body_color || '#94a3b8',
            margin: '0 auto',
            lineHeight: 1.45,
            maxWidth: '300px'
          }}>
            {isLogin 
              ? 'Access your trophy vault, order custom keepsakes & claim cards' 
              : 'Create your digital trophy vault collector account'}
          </p>
        </div>

        {/* Content Panel for Form Card */}
        <div
          data-testid="mobile-auth-panel"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '20px 18px',
            display: 'flex',
            flexDirection: 'column',
            ...customPanelStyle
          }}
        >
          {/* Segmented Switcher: Sign In / Create Account */}
          {allowSignup && (
            <div style={{
              display: 'flex',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '12px',
              padding: '3px',
              marginBottom: '20px'
            }}>
              <button
                type="button"
                onClick={() => { setIsLogin(true); setError(''); }}
                style={{
                  flex: 1,
                  padding: '10px 0',
                  borderRadius: '9px',
                  fontSize: '13px',
                  fontWeight: 700,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  ...(isLogin ? {
                    ...customBadgeStyle,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)'
                  } : {
                    backgroundColor: 'transparent',
                    color: typoConfig?.body_color || '#94a3b8'
                  })
                }}
              >
                Sign In
              </button>
              <button
                type="button"
                onClick={() => { setIsLogin(false); setError(''); }}
                style={{
                  flex: 1,
                  padding: '10px 0',
                  borderRadius: '9px',
                  fontSize: '13px',
                  fontWeight: 700,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  ...(!isLogin ? {
                    ...customBadgeStyle,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)'
                  } : {
                    backgroundColor: 'transparent',
                    color: typoConfig?.body_color || '#94a3b8'
                  })
                }}
              >
                Create Account
              </button>
            </div>
          )}

        {/* Error Notification */}
        {error && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 14px',
            borderRadius: '12px',
            backgroundColor: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid rgba(239, 68, 68, 0.35)',
            color: '#fca5a5',
            fontSize: '12.5px',
            lineHeight: 1.4,
            marginBottom: '16px'
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        {/* Success Message */}
        {message && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 14px',
            borderRadius: '12px',
            backgroundColor: 'rgba(16, 185, 129, 0.15)',
            border: '1px solid rgba(16, 185, 129, 0.35)',
            color: '#6ee7b7',
            fontSize: '12.5px',
            lineHeight: 1.4,
            marginBottom: '16px'
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <span>{message}</span>
          </div>
        )}

        {/* Form Container */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {!isLogin && (
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: '#94a3b8',
                  marginBottom: '6px'
                }}>
                  First Name
                </label>
                <input
                  type="text"
                  required
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="John"
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    backgroundColor: '#121824',
                    border: '1px solid rgba(255, 255, 255, 0.14)',
                    borderRadius: '12px',
                    padding: '12px 14px',
                    fontSize: '14px',
                    color: '#ffffff',
                    outline: 'none'
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  fontSize: '11px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  color: '#94a3b8',
                  marginBottom: '6px'
                }}>
                  Last Name
                </label>
                <input
                  type="text"
                  required
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Doe"
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    backgroundColor: '#121824',
                    border: '1px solid rgba(255, 255, 255, 0.14)',
                    borderRadius: '12px',
                    padding: '12px 14px',
                    fontSize: '14px',
                    color: '#ffffff',
                    outline: 'none'
                  }}
                />
              </div>
            </div>
          )}

          <div>
            <label style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: '#94a3b8',
              marginBottom: '6px'
            }}>
              Email Address
            </label>
            <input
              type="email"
              required
              inputMode="email"
              autoCapitalize="none"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                backgroundColor: '#121824',
                border: '1px solid rgba(255, 255, 255, 0.14)',
                borderRadius: '12px',
                padding: '12px 14px',
                fontSize: '14px',
                color: '#ffffff',
                outline: 'none'
              }}
            />
          </div>

          <div>
            <label style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              color: '#94a3b8',
              marginBottom: '6px'
            }}>
              Password
            </label>
            <div style={{ position: 'relative', width: '100%' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  backgroundColor: '#121824',
                  border: '1px solid rgba(255, 255, 255, 0.14)',
                  borderRadius: '12px',
                  padding: '12px 42px 12px 14px',
                  fontSize: '14px',
                  color: '#ffffff',
                  outline: 'none'
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  padding: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {isLogin && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 0' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: typoConfig?.body_color || '#94a3b8' }}>
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  style={{ accentColor: typoConfig?.accent_color || primaryColor, width: '16px', height: '16px', cursor: 'pointer' }}
                />
                Remember me
              </label>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              marginTop: '8px',
              padding: '13px 18px',
              borderRadius: '12px',
              backgroundColor: typoConfig?.accent_color || primaryColor,
              color: '#ffffff',
              fontSize: '15px',
              fontWeight: 800,
              border: 'none',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: `0 4px 18px ${(typoConfig?.accent_color || primaryColor)}40`,
              transition: 'transform 0.15s ease',
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 1s linear infinite' }}>
                  <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                  <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.25)" strokeWidth="3" />
                  <path d="M12 2a10 10 0 0 1 10 10" stroke="#ffffff" strokeWidth="3" strokeLinecap="round" />
                </svg>
                <span>{isLogin ? 'Signing In...' : 'Creating Account...'}</span>
              </span>
            ) : (
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>{isLogin ? 'Sign In' : 'Create Account'}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </span>
            )}
          </button>
        </form>

        {/* Security Badge with customBadgeStyle */}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '14px' }}>
          <span
            style={{
              ...customBadgeStyle,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 12px',
              fontSize: '11px',
              letterSpacing: '0.04em'
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span>SECURE VAULT ACCESS</span>
          </span>
        </div>
      </div>
    </div>

      {/* Switch to Desktop Site Footer */}
      <div style={{
        paddingTop: '20px',
        textAlign: 'center',
        width: '100%',
        maxWidth: '380px'
      }}>
        <button
          onClick={onSwitchToDesktop}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: '20px',
            padding: '8px 16px',
            fontSize: '12px',
            fontWeight: 600,
            color: typoConfig?.body_color || '#94a3b8',
            cursor: 'pointer',
            transition: 'background-color 0.15s ease'
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
            <line x1="8" y1="21" x2="16" y2="21" />
            <line x1="12" y1="17" x2="12" y2="21" />
          </svg>
          <span>Switch to Desktop Site</span>
        </button>
      </div>
    </div>
  );
}
